import uniqBy from "lodash-es/uniqBy";
import * as Moment from "moment";
import * as React from "react";
import { QueryCondition, QueryFilter } from "../../../misc/api/apiTypes";
import Alert, { AlertCategoryEnum } from "../../../misc/components/Alert";
import { LocalQueryTable } from "../../../misc/components/queryTable/LocalQueryTable";
import MultiSelectModal from "../../../misc/components/queryTable/MultiSelectModal";
import { QueryTableColumn } from "../../../misc/components/queryTable/QueryTable";
import { TimeRangeModal } from "../../../misc/components/queryTable/TimeRangeModal";
import { instanceOfPeaksPoint, PeaksPeriod, PeaksPoint } from "../interfacesPeaks/PeaksJS";
import { formatToHHmmss_sss, HHmmssToTotalSeconds } from "../../../misc/timeFormatting";
import { Query } from '../../../misc/components/queryTable/Query';

interface EventListTableProps {
    timestampColumn: "timestamp" | "startTime" | "rawTimestamp";
    startTimeColumnName: "Start Time" | "Waveform Time";
    withEndTime: boolean;
    events: Array<PeaksPoint | PeaksPeriod>;
    renderRawTime?: boolean;
    onJumpToEventClick: (timepoint: number) => void;
    onDeleteEventClick: (id: string) => void;
    query: Query<any>;
}

interface EventListTableState {
    showEventTypeFilter: boolean;
    showStartTimeFilter: boolean;
    showRawTimeFilter: boolean;
    filters: QueryFilter[];
}

export class EventListTable extends React.Component<EventListTableProps, EventListTableState> {
    childRef: any;

    constructor(props: EventListTableProps) {
        super(props);

        this.state = {
            showEventTypeFilter: false,
            showStartTimeFilter: false,
            showRawTimeFilter: false,
            filters: props.query ? props.query.Filters : [],
        };
    }

    getTableQuery() {
        return this.childRef ? this.childRef.getQuery() : null;
    }

    public renderLabel = (event: PeaksPoint | PeaksPeriod) => {
        return (
            <>
                <i className="fa fa-square" style={{ color: event.color }} />&nbsp;
                {event.eventTypeName}
            </>
        );
    };

    renderHamburgerMenu = (event: PeaksPoint & PeaksPeriod) => {
        return (
            <>
                <button
                    className="btn btn-primary btn-xs"
                    onClick={() =>
                        this.props.onJumpToEventClick(
                            event.isPoint ? event.timestamp : event.startTime
                        )
                    }
                >
                    <i className="fa fa-share" />
                </button>
                &nbsp;
                <button
                    className="btn btn-danger btn-xs"
                    disabled={!event.editable}
                    onClick={() => this.props.onDeleteEventClick(event.id)}
                >
                    <i className="fa fa-trash" />
                </button>
            </>
        );
    };

    handleFilter = (member: keyof (PeaksPoint & PeaksPeriod)) => {
        switch (member) {
            case "eventTypeName":
                this.setState({ showEventTypeFilter: true });
                break;
            case "timestamp":
            case "startTime":
                this.setState({ showStartTimeFilter: true });
                break;
            case "rawTimestamp":
                this.setState({ showRawTimeFilter: true });
                break;
            default:
                break;
        }
    };

    handleFilterClose = () => {
        this.setState({
            showEventTypeFilter: false,
            showStartTimeFilter: false,
            showRawTimeFilter: false
        });
    };

    handleMultiSelectFilterChange = (
        member: keyof (PeaksPoint | PeaksPeriod),
        values: string[]
    ) => {
        let eventTypeNameFilter = this.state.filters.find(x => x.Member === "eventTypeName");
        if (eventTypeNameFilter) {
            if (values.length > 0) {
                eventTypeNameFilter.Values = values;
            } else {
                eventTypeNameFilter = null;
            }
        } else {
            eventTypeNameFilter = {
                Member: "eventTypeName",
                Condition: QueryCondition.Equal,
                Values: values
            };
        }

        const newFilters = [
            ...this.state.filters.filter(x => x.Member !== "eventTypeName"),
            eventTypeNameFilter
        ];

        this.setState({
            filters: newFilters.filter(x => x).filter(x => !x.Values.includes(undefined)) // Get rid of undefined or nulls
        });
    };

    public handleDateRangeFilterChange = (from: string, to: string, member: string) => {
        let fromFilter = this.state.filters.find(
            x => x.Member === member && x.Condition === QueryCondition.GreaterThanOrEqual
        );

        if (fromFilter && from) {
            fromFilter.Values = [from];
        } else if (from === null) {
            fromFilter = null;
        } else {
            fromFilter = {
                Member: member,
                Condition: QueryCondition.GreaterThanOrEqual,
                Values: [from]
            };
        }

        let toFilter = this.state.filters.find(
            x => x.Member === member && x.Condition === QueryCondition.LessThanOrEqual
        );

        if (toFilter && to) {
            toFilter.Values = [to];
        } else if (to === null) {
            toFilter = null;
        } else {
            toFilter = {
                Member: member,
                Condition: QueryCondition.LessThanOrEqual,
                Values: [to]
            };
        }

        const newFilters = [
            ...this.state.filters.filter(x => x.Member !== member),
            fromFilter,
            toFilter
        ];

        this.setState({
            filters: newFilters.filter(x => x).filter(x => !x.Values.includes(undefined)) // Get rid of undefined or nulls
        });
    };

    public handleFilterClear = (member: string) => {
        this.setState({
            filters: this.state.filters.filter(x => x.Member !== member),
        });

        this.handleFilterClose();
    }

    padDatePart = (datePart: number): string => {
        return ("0" + datePart).slice(-2);
    };

    filterDatesCallback = (
        event: PeaksPoint | PeaksPeriod,
        totalFromSeconds: number,
        totalToSeconds: number,
        column: string
    ): boolean => {
        let rawOrRelativeTimestamp;
        if (instanceOfPeaksPoint(event) && column === "rawTimestamp") {
            rawOrRelativeTimestamp = event.rawTimestamp;
        } else if (instanceOfPeaksPoint(event) && column === "timestamp") {
            rawOrRelativeTimestamp = event.timestamp;
        } else if (!instanceOfPeaksPoint(event) && column === "startTime")
            rawOrRelativeTimestamp = event.startTime;

        return (
            rawOrRelativeTimestamp >= totalFromSeconds &&
            rawOrRelativeTimestamp <= totalToSeconds
        );
    };

    filterEventTypes = (
        filterValues: string[]
    ) => {
        let filteredEvents: Array<PeaksPoint | PeaksPeriod>;
        if (filterValues.length > 0) {
            filteredEvents = this.props.events.filter(x => filterValues.includes(x["eventTypeId"]));
        } else {
            filteredEvents = this.props.events;
        }
        return filteredEvents;
    };

    render() {
        if (this.props.events.length === 0) {
            return (
                <Alert show={true} category={AlertCategoryEnum.Success}>
                    There are no events currently marked
                </Alert>
            );
        }

        let {
            filteredEvents,
            eventTypeFilterValues
        } = this.getFilteredEventsAndFilterValues();

        if ((filteredEvents as PeaksPoint[]).some(o => o.rawTimestamp !== undefined))
            filteredEvents = this.filterByTime(filteredEvents, "rawTimestamp");

        filteredEvents = this.filterByTime(filteredEvents, this.props.timestampColumn);

        return (
            <>
                <LocalQueryTable
                    collection={filteredEvents}
                    defaultPageSize={10}
                    onFilter={this.handleFilter}
                    filters={this.state.filters}
                    ref={childRef => {
                        this.childRef = childRef;
                    }}
                    query={this.props.query}
                    rowUniqueIdentifier={row => row.id}
                >
                    <QueryTableColumn
                        member="eventTypeName"
                        header="Event Type"
                        map={(row: PeaksPoint & PeaksPeriod) => this.renderLabel(row)}
                        sortable={true}
                        filterable={true}
                    />
                    {this.renderRawTimeColumn()}
                    <QueryTableColumn
                        member={this.props.timestampColumn}
                        header={this.props.startTimeColumnName}
                        map={(row: PeaksPoint & PeaksPeriod) =>
                            row.isPoint
                                ? formatToHHmmss_sss(row.timestamp)
                                : formatToHHmmss_sss(row.startTime)
                        }
                        sortable={true}
                        filterable={true}
                    />
                    {this.renderEndTimeColumn()}
                    <QueryTableColumn
                        member=""
                        header=""
                        map={row => this.renderHamburgerMenu(row)}
                    />
                </LocalQueryTable>
                {this.renderFilterModals(eventTypeFilterValues)}
            </>
        );
    }
    private renderFilterModals(eventTypeFilterValues: string[]) {
        return (
            <>
                <MultiSelectModal
                    header="Event Type Filter"
                    show={this.state.showEventTypeFilter}
                    options={this.getEventTypeOptions()}
                    valueKey="Value"
                    labelKey="Label"
                    values={eventTypeFilterValues}
                    onChange={values => this.handleMultiSelectFilterChange("eventTypeId", values.map(v => v.Value))}
                    onApply={this.handleFilterClose}
                    onClose={this.handleFilterClose}
                    onClear={() => this.handleFilterClear("eventTypeName")}
                />
                <TimeRangeModal
                    header="Raw Time Filter"
                    show={this.state.showRawTimeFilter}
                    from={this.state.filters.find(x => x.Member === "rawTimestamp" && x.Condition === QueryCondition.GreaterThanOrEqual)?.Values[0] as string}
                    to={this.state.filters.find(x => x.Member === "rawTimestamp" && x.Condition === QueryCondition.LessThanOrEqual)?.Values[0] as string}
                    onChange={(from, to) =>
                        this.handleDateRangeFilterChange(from, to, "rawTimestamp")
                    }
                    onClose={this.handleFilterClose}
                    onClear={() => this.handleFilterClear("rawTimestamp")}
                />
                <TimeRangeModal
                    header={`${this.props.startTimeColumnName} Filter`}
                    show={this.state.showStartTimeFilter}
                    from={this.state.filters.find(x => x.Member === this.props.timestampColumn && x.Condition === QueryCondition.GreaterThanOrEqual)?.Values[0] as string}
                    to={this.state.filters.find(x => x.Member === this.props.timestampColumn && x.Condition === QueryCondition.LessThanOrEqual)?.Values[0] as string}
                    onChange={(from, to) => this.handleDateRangeFilterChange(from, to, this.props.timestampColumn)}
                    onClose={this.handleFilterClose}
                    onClear={() => this.handleFilterClear(this.props.timestampColumn)}
                />
            </>
        );
    }

    private renderRawTimeColumn() {
        return this.props.renderRawTime ? (
            <QueryTableColumn
                member="rawTimestamp"
                header="Raw Time"
                map={(row: PeaksPoint & PeaksPeriod) =>
                    row.isPoint
                        ? formatToHHmmss_sss(row.rawTimestamp)
                        : formatToHHmmss_sss(row.startTime)
                }
                sortable={true}
                filterable={true}
            />
        ) : null;
    }

    private getFilteredEventsForFilteredDates = (
        filteredEvents: Array<PeaksPoint | PeaksPeriod>,
        startTotalSeconds: number,
        endTotalSeconds: number,
        column: string
    ) =>
        filteredEvents.filter(x => this.filterDatesCallback(x, startTotalSeconds, endTotalSeconds, column));

    private filterByTime(filteredEvents: Array<PeaksPoint | PeaksPeriod>, column: string) {

        const startFilter = this.state.filters.find(x => x.Member === column && x.Condition === QueryCondition.GreaterThanOrEqual)?.Values[0] as string;
        const endFilter = this.state.filters.find(x => x.Member === column && x.Condition === QueryCondition.LessThanOrEqual)?.Values[0] as string;

        if ((startFilter !== undefined && startFilter !== "")|| (endFilter !== undefined && endFilter !== "")) {
            filteredEvents = this.getFilteredEventsForFilteredDates(
                filteredEvents,
                HHmmssToTotalSeconds(startFilter),
                HHmmssToTotalSeconds(endFilter),
                column
            );
        }
        return filteredEvents;
    }

    private getFilteredEventsAndFilterValues() {
        let filteredEvents: Array<PeaksPoint | PeaksPeriod> = [];

        const eventTypeFilterValues = [
            ...this.state.filters
                .filter(x => x.Member === "eventTypeName")
                .map(y => y.Values.map(v => String(v))).flat()
        ];

        filteredEvents = this.filterEventTypes(eventTypeFilterValues);

        return { filteredEvents, eventTypeFilterValues };
    }

    private getEventTypeOptions() {
        return uniqBy(this.props.events, e => e.eventTypeId).map(m => {
            return { Label: m.eventTypeName, Value: m.eventTypeId };
        });
    }

    private renderEndTimeColumn() {
        return this.props.withEndTime ? (
            <QueryTableColumn
                member="endTime"
                header="End Time"
                map={(row: PeaksPoint & PeaksPeriod) =>
                    row.isPoint ? "" : formatToHHmmss_sss(row.endTime)
                }
                sortable={true}
                filterable={false}
            />
        ) : null;
    }
}
